import { useState } from 'react'
import styled from 'styled-components'

import { EVENT_SURVEY_SCALE_MAX, EVENT_SURVEY_SCALE_MIN } from '../../../../services/Event/surveyTypes'
import { magenta } from '../../../../theme'
import { IEventSurveyQuestionDraft, replaceEventName, SAMPLE_RECIPIENT_NAME } from './eventSurveyForm'

/*
 * Prévia da página pública da pesquisa NPS do evento (ABRA-370), no visual escuro das páginas de
 * concurso do site (site-abra-v2 · concurso/styles/contest.styles.ts) — o mesmo recurso que o
 * `ArtFieldsPreview` usa para o formulário de obras. É interativa (dá para marcar uma nota e
 * digitar), mas não envia nada.
 */

const PINK = magenta[500]

const Page = styled.div<{ $imageUrl?: string | null }>`
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.8)), url("${$imageUrl}") center / cover no-repeat`
      : 'linear-gradient(to bottom, black, #1a0000, black)'};
  border-radius: 16px;
  padding: 24px 16px;
  color: rgba(255, 255, 255, 0.7);
  font-family: var(--font-georama), 'Georama', sans-serif;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: #070707;
  border: 1px solid #292929;
  border-radius: 24px;
  padding: 24px;
  max-width: 640px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const LogoBox = styled.div`
  background: linear-gradient(to bottom right, #ac004b7e, #5800257e);
  border-radius: 20px;
  border: 1px solid #fa006870;
  overflow: hidden;
  aspect-ratio: 16 / 6;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const LogoFallback = styled.span`
  color: #fff;
  font-weight: 700;
  font-size: 1.5rem;
  letter-spacing: 0.08em;
`

const Title = styled.h3`
  color: #fff;
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
`

const Accent = styled.span`
  color: ${PINK};
`

const Greeting = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #292929;
  width: 100%;
  margin: 0;
`

const Question = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Statement = styled.legend`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9375rem;
  line-height: 1.5;
  padding: 0;
  margin-bottom: 12px;
`

const Required = styled.span`
  color: ${PINK};
  margin-left: 4px;
`

const Scale = styled.div`
  display: grid;
  grid-template-columns: repeat(11, minmax(0, 1fr));
  gap: 6px;
`

const ScaleButton = styled.button<{ $selected: boolean }>`
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${({ $selected }) => ($selected ? PINK : '#292929')};
  background: ${({ $selected }) => ($selected ? `linear-gradient(to bottom right, ${PINK}, #ac004b)` : 'transparent')};
  color: ${({ $selected }) => ($selected ? '#fff' : 'rgba(255, 255, 255, 0.8)')};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  &:hover {
    border-color: ${PINK};
  }
`

const ScaleLegend = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`

const DarkTextarea = styled.textarea`
  background-color: transparent;
  border: 1px solid #292929;
  border-radius: 8px;
  padding: 10px 14px;
  color: rgba(255, 255, 255, 0.85);
  width: 100%;
  min-height: 100px;
  resize: vertical;
  font-size: 0.9375rem;
  font-family: inherit;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  &:focus,
  &:hover {
    border-color: ${PINK};
  }
`

const PinkButton = styled.div`
  background: linear-gradient(to bottom right, ${PINK}, #ac004b);
  color: #fff;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  opacity: 0.85;
`

const Empty = styled.p`
  margin: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.875rem;
`

const SCALE_VALUES = Array.from(
  { length: EVENT_SURVEY_SCALE_MAX - EVENT_SURVEY_SCALE_MIN + 1 },
  (_, index) => EVENT_SURVEY_SCALE_MIN + index
)

interface IEventSurveyPreviewProps {
  questions: IEventSurveyQuestionDraft[]
  eventName: string
  eventImage?: string | null
}

export default function EventSurveyPreview({ questions, eventName, eventImage }: IEventSurveyPreviewProps) {
  // Estado descartável, só para a prévia responder ao clique como a página de verdade.
  const [scores, setScores] = useState<Record<string, number>>({})

  return (
    <Page $imageUrl={eventImage}>
      <Card>
        <Header>
          <LogoBox>{eventImage ? <img src={eventImage} alt="" /> : <LogoFallback>ABRA</LogoFallback>}</LogoBox>
          <Title>
            Pesquisa de Satisfação — <Accent>{eventName}</Accent>
          </Title>
          <Greeting>
            Olá, {SAMPLE_RECIPIENT_NAME}! Queremos saber como foi a sua experiência. A pesquisa leva menos de 1 minuto.
          </Greeting>
        </Header>

        <Divider />

        {questions.length === 0 && <Empty>Adicione perguntas para ver o formulário.</Empty>}

        {questions.map((question) => (
          <Question key={question.clientKey}>
            <Statement>
              {replaceEventName(question.statement, eventName) || 'Enunciado da pergunta'}
              {question.is_required && <Required aria-hidden>*</Required>}
            </Statement>

            {question.type === 'scale' ? (
              <>
                <Scale>
                  {SCALE_VALUES.map((value) => (
                    <ScaleButton
                      key={value}
                      type="button"
                      $selected={scores[question.clientKey] === value}
                      onClick={() => setScores((prev) => ({ ...prev, [question.clientKey]: value }))}
                    >
                      {value}
                    </ScaleButton>
                  ))}
                </Scale>
                {(question.min_label || question.max_label) && (
                  <ScaleLegend>
                    <span>{question.min_label}</span>
                    <span>{question.max_label}</span>
                  </ScaleLegend>
                )}
              </>
            ) : (
              <DarkTextarea placeholder="Escreva sua resposta" maxLength={5000} />
            )}
          </Question>
        ))}

        <PinkButton>Enviar respostas</PinkButton>
      </Card>
    </Page>
  )
}
